'use client'

import { Clock, MapPin, Navigation, Phone } from "lucide-react"
import { resolveColorHex, getVendorPrimaryColor, formatImageURL } from "@/utils/util"
import { getBranchOpenStatus, summariseWorkingHours } from "@/utils/branch"

export default function BranchCard({ branch, vendor }: { branch: any, vendor: any, index?: number }) {
  const primary = resolveColorHex(getVendorPrimaryColor(vendor))
  const status = getBranchOpenStatus(branch?.workingHours)
  const hoursRows = summariseWorkingHours(branch?.workingHours)

  return (
    <div className="rounded-2xl border border-border bg-white overflow-hidden flex flex-col">
      <div className="aspect-16/9 bg-muted relative overflow-hidden">
        <img
          src={formatImageURL(branch?.image || 'placeholder.png')}
          alt={branch?.name || ''}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <div className="text-[18px] font-semibold tracking-[-0.01em] text-foreground flex items-center gap-2 flex-wrap">
              {branch.name}
              {branch.isDefault && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-[0.1em] uppercase px-2 py-0.5 rounded-full"
                  style={{
                    color: primary,
                    background: `color-mix(in oklch, ${primary} 10%, white)`,
                    border: `1px solid color-mix(in oklch, ${primary} 20%, transparent)`,
                  }}
                >
                Үндсэн
                </span>
              )}
            </div>
          </div>
          {status.open ? (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-1 rounded-full shrink-0">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />Нээлттэй
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground bg-muted px-2 py-1 rounded-full shrink-0">
              <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" />Хаалттай
            </span>
          )}
        </div>

        {branch.address && (
          <div className="text-[13.5px] leading-relaxed text-foreground">
            {branch.address}
          </div>
        )}

        {branch.latitude && branch.longitude && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${branch.latitude},${branch.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1.5 text-[12px] text-muted-foreground inline-flex items-center gap-1.5 hover:text-foreground transition-colors w-fit"
          >
            <Navigation className="h-3 w-3" />
          Газрын зураг дээр харах
          </a>
        )}

        <div className="mt-4 pt-4 border-t border-border flex flex-col gap-2 text-[12.5px]">
          {(branch.phone || (Array.isArray(branch.additionalPhones) && branch.additionalPhones.length > 0)) && (() => {
            const phones = [branch.phone, ...(Array.isArray(branch.additionalPhones) ? branch.additionalPhones : [])].filter(Boolean)
            return (
              <div className="flex items-start gap-2">
                <Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  {phones.map((p, i) => (
                    <span key={i} className="inline-flex items-center gap-2">
                      <a href={`tel:${p}`} className="font-medium hover:underline">{p}</a>
                      {i < phones.length - 1 && <span className="text-muted-foreground">·</span>}
                    </span>
                  ))}
                </div>
              </div>
            )
          })()}
          {hoursRows.length > 0 && (
            <div className="flex items-start gap-2">
              <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                {hoursRows.map((r, i) => (
                  <div key={i} className="flex items-baseline justify-between gap-3">
                    <span className="text-muted-foreground">{r.day}</span>
                    <span className="font-medium tabular-nums">{r.time}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-auto pt-4 flex items-center gap-2">
          {branch.phone ? (
            <a
              href={`tel:${branch.phone}`}
              className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-lg border border-border text-[13px] font-medium hover:bg-muted transition-colors"
            >
              <Phone className="h-4 w-4" />Залгах
            </a>
          ) : null}
          {(branch?.address || (branch?.latitude && branch?.longitude)) && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                branch?.latitude && branch?.longitude
                  ? `${branch.latitude},${branch.longitude}`
                  : branch.address
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-lg text-white text-[13px] font-medium hover:opacity-95 transition-opacity"
              style={{ background: primary }}
            >
              <MapPin className="h-4 w-4" />
            Газрын зураг
            </a>
          )}
        </div>
      </div>
    </div>
  )
}
